/**
 * V2.0.39 — Architect Hand-off Dialog.
 *
 * Captures the architect's email + an optional note, then hits
 * POST /api/designs/{id}/share-with-architect which builds the IFC +
 * Architectural PDF + (optional) acceptance log and ships them via
 * Resend. The architect's email lands in `architect_referrals` and
 * powers the B2B referral flywheel.
 *
 * Triggered by the "Email it to your architect" toast action — which
 * itself is gated behind:
 *   - Architect-tier project_unlock present on the design
 *   - 5 consecutive clean IFC validation ticks
 *   - One-shot per design (localStorage flag)
 */
import React, { useState } from "react";
import { X, Mail, Loader2, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { shareDesignWithArchitect } from "../lib/api";

export default function ArchitectHandoffDialog({ open, designId, designName, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const canSend = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(email.trim()) && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const r = await shareDesignWithArchitect(designId, {
        recipient_email: email.trim(),
        recipient_name: name.trim() || null,
        message: message.trim() || null,
      });
      if (r.data?.success) {
        toast.success(
          `Sent to ${r.data.recipient_email}`,
          { description: "Your architect now has the IFC + PDF in their inbox." },
        );
        onClose?.();
        setEmail("");
        setName("");
        setMessage("");
      } else {
        toast.error("Send failed — please try again.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "architect_tier_required") {
        toast.error("Architect-tier unlock required to share the IFC.");
      } else {
        toast.error("Send failed — please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="architect-handoff-dialog"
      className="fixed inset-0 z-[100] grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-zinc-950 border-2 border-brass-500 shadow-[0_0_60px_-20px_rgba(196,169,107,0.45)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
              // Architect Hand-off
            </div>
            <div className="mt-0.5 font-display text-zinc-50 text-base">
              Email this design to your architect
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="architect-handoff-close"
            className="text-zinc-500 hover:text-zinc-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-3.5">
          <div className="flex items-start gap-2 text-[11px] text-zinc-400 leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 text-brass-400 flex-shrink-0 mt-0.5" />
            We'll attach the IFC 4 BIM file + Architectural PDF + acceptance log
            and send them in a single email. Your architect can open the IFC
            in Revit / ArchiCAD / Vectorworks directly.
          </div>

          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
              Architect's email <span className="text-rose-400">*</span>
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="architect@firm.com"
              autoFocus
              data-testid="architect-handoff-email"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-3 py-2.5 font-mono text-sm text-zinc-100 placeholder:text-zinc-600"
            />
          </label>

          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
              Their name <span className="text-zinc-700">(optional)</span>
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Architects"
              data-testid="architect-handoff-name"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-3 py-2.5 font-mono text-sm text-zinc-100 placeholder:text-zinc-600"
            />
          </label>

          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
              Short note <span className="text-zinc-700">(optional)</span>
            </div>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please review the egress windows on the rear bedrooms…"
              maxLength={500}
              data-testid="architect-handoff-message"
              className="w-full bg-zinc-900 border border-zinc-800 focus:border-brass-500 outline-none px-3 py-2.5 font-mono text-xs text-zinc-100 placeholder:text-zinc-600 resize-none"
            />
          </label>

          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
            // Sent as you · reply-to your account email · {designName || "design"}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-zinc-800 flex items-center justify-between gap-3 bg-zinc-950">
          <button
            type="button"
            onClick={onClose}
            data-testid="architect-handoff-cancel"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            data-testid="architect-handoff-send"
            className={
              "inline-flex items-center gap-2 px-5 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors " +
              (canSend
                ? "bg-brass-500 hover:bg-brass-400 text-zinc-950"
                : "bg-zinc-900 text-zinc-600 cursor-not-allowed")
            }
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? "Sending…" : "Send to architect"}
          </button>
        </div>
      </div>
    </div>
  );
}
